import { Box, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { AppContext } from "@/models/AppStateProvider.tsx";
import type { ActiveSpanInfo } from "@/models/TelemetryProvider";
import { useTelemetry } from "@/models/TelemetryProvider";
import { applyFeedEviction, type EvictionResult } from "@/services/FeedEviction";
import { ResetCoordinator } from "@/services/ResetCoordinator";
import DebugMetricsModal from "./DebugMetricsModal";
import LogChannelsModal from "./LogChannelsModal";
import QueryMetricsModal from "./QueryMetricsModal";
import QueryTimelineModal from "./QueryTimelineModal";
import { seedDebugTags } from "./seedDebugTags";

const formatDuration = (ms: number): string => {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(1)}s`;
};

const formatActionName = (action: string): string => {
  const parts = action.split(".");
  if (parts.length > 1) {
    return parts.slice(-2).join(".");
  }
  return action;
};

const formatTarget = (target?: string): string => {
  if (!target) return "";
  if (target.length > 30) {
    return target.substring(0, 30) + "...";
  }
  return target;
};

export default function DebugPanel() {
  const { flags } = useContext(AppContext);
  const telemetry = useTelemetry();
  const [activeSpans, setActiveSpans] = useState<Map<string, ActiveSpanInfo>>(new Map());
  const [, setUpdateCounter] = useState(0);
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [showQueryMetricsModal, setShowQueryMetricsModal] = useState(false);
  const [showLogChannelsModal, setShowLogChannelsModal] = useState(false);
  const [showQueryTimelineModal, setShowQueryTimelineModal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [checkpointState, setCheckpointState] = useState<"idle" | "running" | "done" | "error">("idle");
  const checkpointResetTimer = useRef<number | null>(null);
  const [evictionState, setEvictionState] = useState<"idle" | "running" | EvictionResult | "error">("idle");
  const evictionResetTimer = useRef<number | null>(null);
  const [seedTagsState, setSeedTagsState] = useState<"idle" | "running" | "done" | "error">("idle");
  const seedTagsResetTimer = useRef<number | null>(null);

  const handleSeedTags = useCallback(async () => {
    if (seedTagsState === "running") return;
    setSeedTagsState("running");
    try {
      await seedDebugTags();
      setSeedTagsState("done");
    } catch {
      setSeedTagsState("error");
    }
    if (seedTagsResetTimer.current) window.clearTimeout(seedTagsResetTimer.current);
    seedTagsResetTimer.current = window.setTimeout(() => setSeedTagsState("idle"), 5000);
  }, [seedTagsState]);

  const handleCheckpoint = useCallback(async () => {
    if (checkpointState === "running") return;
    setCheckpointState("running");
    try {
      await ResetCoordinator.getInstance().forceCheckpoint();
      setCheckpointState("done");
    } catch {
      setCheckpointState("error");
    }
    if (checkpointResetTimer.current) window.clearTimeout(checkpointResetTimer.current);
    checkpointResetTimer.current = window.setTimeout(() => setCheckpointState("idle"), 5000);
  }, [checkpointState]);

  const handleEvict = useCallback(async () => {
    if (evictionState === "running") return;
    setEvictionState("running");
    try {
      const result = await applyFeedEviction();
      setEvictionState(result);
    } catch {
      setEvictionState("error");
    }
    if (evictionResetTimer.current) window.clearTimeout(evictionResetTimer.current);
    evictionResetTimer.current = window.setTimeout(() => setEvictionState("idle"), 5000);
  }, [evictionState]);

  useEffect(() => {
    const updateSpans = () => {
      setActiveSpans(telemetry.getActiveSpans());
      setUpdateCounter((c) => c + 1);
    };

    updateSpans();
    const unsubscribe = telemetry.subscribeToSpanUpdates(updateSpans);

    const interval = setInterval(() => {
      setUpdateCounter((c) => c + 1);
    }, 100);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [telemetry]);

  const spans = Array.from(activeSpans.values());
  const now = Date.now();

  const collapsedValues = flags?.newDesign ? { left: "70px", bottom: "10px" } : { right: "10px", top: "10px" };

  // Collapsed view
  if (isCollapsed) {
    return (
      <>
        <Box
          onClick={() => setIsCollapsed(false)}
          sx={{
            position: "fixed",
            zIndex: 9999,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            color: "#0f0",
            padding: "4px 8px",
            borderRadius: "3px",
            boxShadow: "-4px 4px 12px rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            border: "1px solid #0f0",
            userSelect: "none",
            pointerEvents: "auto",
            "&:hover": {
              backgroundColor: "rgba(0, 255, 0, 0.15)",
              borderColor: "#0ff",
            },
            ...collapsedValues,
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "9px",
                fontWeight: "600",
                color: "#0f0",
                lineHeight: 1,
                fontFamily: "monospace",
              }}
            >
              {"</>"}
            </Typography>
            {spans.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  minWidth: "12px",
                  height: "12px",
                  backgroundColor: "#f00",
                  borderRadius: "50%",
                  border: "1px solid rgba(0, 0, 0, 0.9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 2px",
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.6, transform: "scale(0.9)" },
                    "100%": { opacity: 1, transform: "scale(1)" },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "8px",
                    fontWeight: "bold",
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  {spans.length}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <DebugMetricsModal open={showMetricsModal} onClose={() => setShowMetricsModal(false)} />
        <QueryMetricsModal open={showQueryMetricsModal} onClose={() => setShowQueryMetricsModal(false)} />
        <QueryTimelineModal open={showQueryTimelineModal} onClose={() => setShowQueryTimelineModal(false)} />
        <LogChannelsModal open={showLogChannelsModal} onClose={() => setShowLogChannelsModal(false)} />
      </>
    );
  }

  const expandedViewValues = flags?.newDesign
    ? {
        left: "60px",
        bottom: 0,
      }
    : {
        right: 0,
        top: 0,
      };

  // Expanded view
  return (
    <>
      <Box
        sx={{
          position: "fixed",
          zIndex: 9999,
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          color: "#0f0",
          padding: "8px 10px",
          fontSize: "11px",
          fontFamily: "monospace",
          maxWidth: "400px",
          borderRadius: "3px",
          boxShadow: "-4px 4px 12px rgba(0, 0, 0, 0.6)",
          border: "1px solid rgba(0, 255, 0, 0.2)",
          userSelect: "none",
          pointerEvents: "auto",
          ...expandedViewValues,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Box
              component="button"
              onClick={() => setShowMetricsModal(true)}
              sx={{
                backgroundColor: "rgba(0, 255, 0, 0.05)",
                border: "1px solid #0f0",
                color: "#0f0",
                fontSize: "9px",
                padding: "4px 8px",
                cursor: "pointer",
                borderRadius: "3px",
                fontFamily: "monospace",
                fontWeight: "600",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(0, 255, 0, 0.15)",
                  borderColor: "#0ff",
                  color: "#0ff",
                },
              }}
            >
              Actions
            </Box>
            <Box
              component="button"
              onClick={() => setShowQueryMetricsModal(true)}
              sx={{
                backgroundColor: "rgba(0, 255, 0, 0.05)",
                border: "1px solid #0f0",
                color: "#0f0",
                fontSize: "9px",
                padding: "4px 8px",
                cursor: "pointer",
                borderRadius: "3px",
                fontFamily: "monospace",
                fontWeight: "600",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(0, 255, 0, 0.15)",
                  borderColor: "#0ff",
                  color: "#0ff",
                },
              }}
            >
              Queries
            </Box>
            <Box
              component="button"
              onClick={() => setShowQueryTimelineModal(true)}
              sx={{
                backgroundColor: "rgba(0, 255, 0, 0.05)",
                border: "1px solid #0f0",
                color: "#0f0",
                fontSize: "9px",
                padding: "4px 8px",
                cursor: "pointer",
                borderRadius: "3px",
                fontFamily: "monospace",
                fontWeight: "600",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(0, 255, 0, 0.15)",
                  borderColor: "#0ff",
                  color: "#0ff",
                },
              }}
            >
              Timeline
            </Box>
            <Box
              component="button"
              onClick={() => setShowLogChannelsModal(true)}
              sx={{
                backgroundColor: "rgba(0, 255, 0, 0.05)",
                border: "1px solid #0f0",
                color: "#0f0",
                fontSize: "9px",
                padding: "4px 8px",
                cursor: "pointer",
                borderRadius: "3px",
                fontFamily: "monospace",
                fontWeight: "600",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(0, 255, 0, 0.15)",
                  borderColor: "#0ff",
                  color: "#0ff",
                },
              }}
            >
              Logs
            </Box>
            <Box
              component="button"
              onClick={handleEvict}
              sx={{
                backgroundColor:
                  evictionState === "running"
                    ? "rgba(255, 165, 0, 0.15)"
                    : evictionState === "error"
                      ? "rgba(255, 0, 0, 0.15)"
                      : typeof evictionState === "object"
                        ? "rgba(0, 255, 0, 0.15)"
                        : "rgba(0, 255, 0, 0.05)",
                border: `1px solid ${evictionState === "error" ? "#f00" : "#0f0"}`,
                color: evictionState === "error" ? "#f00" : "#0f0",
                fontSize: "9px",
                padding: "4px 8px",
                cursor: evictionState === "running" ? "wait" : "pointer",
                borderRadius: "3px",
                fontFamily: "monospace",
                fontWeight: "600",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(0, 255, 0, 0.15)",
                  borderColor: "#0ff",
                  color: "#0ff",
                },
              }}
              title="Run feed eviction"
            >
              {evictionState === "running"
                ? "..."
                : evictionState === "error"
                  ? "ERR"
                  : typeof evictionState === "object"
                    ? `${evictionState.deletedItems}i ${evictionState.deletedContentIds}c`
                    : "Evict"}
            </Box>
            <Box
              component="button"
              onClick={handleSeedTags}
              sx={{
                backgroundColor:
                  seedTagsState === "running"
                    ? "rgba(255, 165, 0, 0.15)"
                    : seedTagsState === "error"
                      ? "rgba(255, 0, 0, 0.15)"
                      : seedTagsState === "done"
                        ? "rgba(0, 255, 0, 0.15)"
                        : "rgba(0, 255, 0, 0.05)",
                border: `1px solid ${seedTagsState === "error" ? "#f00" : "#0f0"}`,
                color: seedTagsState === "error" ? "#f00" : "#0f0",
                fontSize: "9px",
                padding: "4px 8px",
                cursor: seedTagsState === "running" ? "wait" : "pointer",
                borderRadius: "3px",
                fontFamily: "monospace",
                fontWeight: "600",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(0, 255, 0, 0.15)",
                  borderColor: "#0ff",
                  color: "#0ff",
                },
              }}
              title="Seed fake tag data for all workspace memberships"
            >
              {seedTagsState === "running"
                ? "..."
                : seedTagsState === "error"
                  ? "ERR"
                  : seedTagsState === "done"
                    ? "Seeded"
                    : "Seed Tags"}
            </Box>
            <Box
              component="button"
              onClick={handleCheckpoint}
              sx={{
                backgroundColor:
                  checkpointState === "running"
                    ? "rgba(255, 165, 0, 0.15)"
                    : checkpointState === "error"
                      ? "rgba(255, 0, 0, 0.15)"
                      : checkpointState === "done"
                        ? "rgba(0, 255, 0, 0.15)"
                        : "rgba(0, 255, 0, 0.05)",
                border: `1px solid ${checkpointState === "error" ? "#f00" : "#0f0"}`,
                color: checkpointState === "error" ? "#f00" : "#0f0",
                fontSize: "9px",
                padding: "4px 8px",
                cursor: checkpointState === "running" ? "wait" : "pointer",
                borderRadius: "3px",
                fontFamily: "monospace",
                fontWeight: "600",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(0, 255, 0, 0.15)",
                  borderColor: "#0ff",
                  color: "#0ff",
                },
              }}
              title="Force WAL checkpoint"
            >
              {checkpointState === "running" ? "..." : checkpointState === "error" ? "ERR" : "CKPT"}
            </Box>
            <Box
              component="button"
              onClick={() => setIsCollapsed(true)}
              sx={{
                backgroundColor: "rgba(0, 255, 0, 0.05)",
                border: "1px solid #0f0",
                color: "#0f0",
                fontSize: "9px",
                padding: "4px 8px",
                cursor: "pointer",
                borderRadius: "3px",
                fontFamily: "monospace",
                fontWeight: "600",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(0, 255, 0, 0.15)",
                  borderColor: "#0ff",
                  color: "#0ff",
                },
              }}
              title="Collapse"
            >
              ✕
            </Box>
          </Box>

          {/* Operations Section */}
          {spans.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {spans.map((spanInfo) => {
                const duration = now - spanInfo.startTime;
                const actionName = formatActionName(spanInfo.action);
                const targetStr = formatTarget(spanInfo.target);

                return (
                  <Box
                    key={`${spanInfo.action}#${spanInfo.target}`}
                    sx={{
                      padding: "6px 8px",
                      backgroundColor: "rgba(0, 255, 0, 0.03)",
                      borderLeft: "2px solid #0f0",
                      borderRadius: "3px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        marginBottom: targetStr ? "4px" : 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: "6px",
                          height: "6px",
                          backgroundColor: "#0f0",
                          borderRadius: "50%",
                          flexShrink: 0,
                          animation: "pulse 1.5s ease-in-out infinite",
                          "@keyframes pulse": {
                            "0%": { opacity: 1, transform: "scale(1)" },
                            "50%": { opacity: 0.4, transform: "scale(0.85)" },
                            "100%": { opacity: 1, transform: "scale(1)" },
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: "600",
                          color: "#fff",
                          flex: 1,
                        }}
                      >
                        {actionName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "10px",
                          color: "#0f0",
                          fontWeight: "500",
                          backgroundColor: "rgba(0, 255, 0, 0.1)",
                          padding: "1px 6px",
                          borderRadius: "2px",
                        }}
                      >
                        {formatDuration(duration)}
                      </Typography>
                    </Box>
                    {targetStr && (
                      <Typography
                        sx={{
                          fontSize: "9px",
                          color: "#888",
                          marginLeft: "14px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {targetStr}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <DebugMetricsModal open={showMetricsModal} onClose={() => setShowMetricsModal(false)} />
      <QueryMetricsModal open={showQueryMetricsModal} onClose={() => setShowQueryMetricsModal(false)} />
      <QueryTimelineModal open={showQueryTimelineModal} onClose={() => setShowQueryTimelineModal(false)} />
      <LogChannelsModal open={showLogChannelsModal} onClose={() => setShowLogChannelsModal(false)} />
    </>
  );
}
